import { Link, useLocation } from "react-router-dom";
import { Play, ArrowRight, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Same asset as Landing.jsx — cropped via background-size/position
// to isolate the robotic-arm/conveyor scene from the mockup image.
const HERO_ART =
  "https://customer-assets.emergentagent.com/job_channel-analyzer-33/artifacts/tfkxo8by_New%20Factory%20UI.png";

const DENIED_REASONS = {
  not_authorized: "Access not authorized. Ask the admin to add your Google email to the allowlist.",
  email_unverified: "Your Google email is not verified.",
  token_exchange_failed: "Could not exchange the Google authorization code. Try again.",
  userinfo_failed: "Could not fetch your Google profile. Try again.",
};

const TRUST_BADGES = ["No credit card required", "Cancel anytime", "Trusted by creators"];

function Logo() {
  return (
    <Link
      to="/"
      data-testid="login-logo"
      className="inline-flex items-center gap-2 group"
    >
      <span
        aria-hidden
        className="w-9 h-9 rounded-md bg-yellow-400 grid place-items-center shadow-[0_0_24px_-8px_rgba(250,204,21,0.8)] group-hover:shadow-[0_0_28px_-4px_rgba(250,204,21,0.9)] transition-shadow"
      >
        <Play className="w-4 h-4 text-black fill-black -ml-0.5" strokeWidth={0} />
      </span>
      <span className="font-display font-black text-xl text-white tracking-tight">
        YT<span className="text-yellow-400">Maker</span>.AI
      </span>
    </Link>
  );
}

export default function Login() {
  const { user } = useAuth();
  const location = useLocation();
  const stateDenied = location.state?.denied;
  const searchParams = new URLSearchParams(location.search);
  const queryDenied = searchParams.get("denied");
  const denied = stateDenied || (queryDenied && (DENIED_REASONS[queryDenied] || queryDenied));

  // Emergent-managed Google Auth — redirects to Emergent auth, which
  // returns here with #session_id=... in the URL hash; AuthContext
  // exchanges it for a session cookie via POST /api/auth/session.
  const handleLogin = () => {
    const redirect = `${window.location.origin}/journey`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  if (user) {
    window.location.href = "/journey";
    return null;
  }

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-full bg-black text-zinc-100 selection:bg-yellow-400 selection:text-black"
    >
      {/* Sticky nav — matches Landing's chrome so /login feels like the same product */}
      <header
        data-testid="login-nav"
        className="sticky top-0 z-30 backdrop-blur-md bg-black/70 border-b border-zinc-900/80"
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            data-testid="login-back-link"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-300 transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </header>

      {/* Hero — mirrors the Landing hero's grid, subs the CTAs for Google sign-in */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-[65%] opacity-40"
          style={{
            background:
              "radial-gradient(60% 55% at 70% 45%, rgba(250,204,21,0.14), transparent 70%)",
          }}
        />

        <div className="max-w-7xl mx-auto px-6 pt-16 pb-8 lg:pt-24 lg:pb-16 grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — copy + sign-in CTA */}
          <div className="lg:col-span-6 space-y-8 relative z-10">
            <span
              data-testid="login-eyebrow"
              className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-400 border border-yellow-400/40 bg-yellow-400/5 rounded-full px-4 py-2"
            >
              Automated. Faceless. Scalable.
            </span>

            <h1
              data-testid="login-hero-title"
              className="font-display font-black tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
            >
              Sign in to your
              <br />
              <span className="text-yellow-400">content factory.</span>
            </h1>

            <p className="text-lg text-zinc-400 max-w-xl leading-relaxed">
              One click and you&apos;re back on the line — every niche
              scan, storyboard, and stitched upload right where you left
              them. YTMaker.AI signs you in via Google. No passwords
              stored on our side.
            </p>

            {denied && (
              <div
                data-testid="login-denied-banner"
                className="border border-red-500/40 bg-red-500/10 p-3 rounded-lg font-mono text-xs text-red-300 leading-relaxed max-w-xl"
              >
                {denied}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button
                data-testid="google-login-btn"
                onClick={handleLogin}
                className="inline-flex items-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-base px-6 py-3.5 rounded-lg transition-colors shadow-[0_0_36px_-10px_rgba(250,204,21,0.95)] hover:shadow-[0_0_44px_-6px_rgba(250,204,21,1)]"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62z" />
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.87-3.06.87-2.35 0-4.34-1.59-5.05-3.72H.96v2.34A9 9 0 009 18z" />
                  <path fill="#FBBC05" d="M3.95 10.71A5.41 5.41 0 013.65 9c0-.59.1-1.17.3-1.71V4.95H.96A9 9 0 000 9c0 1.45.35 2.83.96 4.05l2.99-2.34z" />
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A9 9 0 009 0 9 9 0 00.96 4.95l2.99 2.34C4.66 5.17 6.65 3.58 9 3.58z" />
                </svg>
                Continue with Google
                <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/"
                data-testid="login-secondary-cta"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-yellow-400/60 text-zinc-200 hover:text-yellow-300 text-base px-6 py-3.5 rounded-lg transition-colors"
              >
                <span className="w-7 h-7 rounded-full border border-current grid place-items-center">
                  <Play className="w-3 h-3 fill-current" strokeWidth={0} />
                </span>
                Watch how it works
              </Link>
            </div>

            <ul className="flex flex-wrap gap-x-6 gap-y-2 pt-2 text-sm text-zinc-400">
              {TRUST_BADGES.map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-yellow-400" />
                  {t}
                </li>
              ))}
            </ul>

            <div className="pt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 leading-relaxed max-w-xl">
              By continuing you agree to operate ethically and not harass the channels surfaced by this system.
            </div>
          </div>

          {/* RIGHT — hero art in the SAME landscape aspect as the Landing hero */}
          <div
            data-testid="login-hero-art"
            role="img"
            aria-label="AI robotic arm assembling YouTube video components on a conveyor belt"
            className="lg:col-span-6 relative aspect-[4/3] lg:aspect-[5/4] rounded-2xl overflow-hidden bg-black"
            style={{
              backgroundImage: `url(${HERO_ART})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "340% auto",
              backgroundPosition: "76% 22%",
            }}
          >
            <div
              aria-hidden
              className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent"
            />
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
